import { z } from 'zod';
import { isoDateSchema, isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * Invoicing: income the business bills itself.
 *
 * Money is a string of cents throughout. A draft is edited freely; an issued
 * invoice is read-only and is voided with a reason, never changed.
 */
export const INVOICE_STATUSES = [
    'draft',
    'sent',
    'partially_paid',
    'paid',
    'void',
    'written_off',
];
export const invoiceStatusSchema = z.enum(INVOICE_STATUSES);
export const INVOICE_STATUS_LABELS = {
    draft: 'Draft',
    sent: 'Issued, unpaid',
    partially_paid: 'Partly paid',
    paid: 'Paid',
    void: 'Voided',
    written_off: 'Written off',
};
const minor = z.string().regex(/^-?\d{1,15}$/);
export const invoiceRowSchema = z.object({
    id: uuidSchema,
    invoiceNumber: z.string().nullable(),
    status: invoiceStatusSchema,
    customerId: uuidSchema,
    customerName: z.string(),
    jobId: uuidSchema.nullable(),
    issuedOn: isoDateSchema,
    dueOn: isoDateSchema.nullable(),
    grossMinor: minor,
    taxMinor: minor,
    netMinor: minor,
    receivedMinor: minor,
    owedMinor: minor,
    /** Positive when past due, negative inside terms, null when not outstanding. */
    daysOverdue: z.number().int().nullable(),
    transactionId: uuidSchema.nullable(),
    createdAt: isoDateTimeSchema,
});
export const invoiceLineSchema = z.object({
    id: uuidSchema,
    lineNumber: z.number().int(),
    description: z.string(),
    /** Numeric as a string, three decimals at most. */
    quantity: z.string(),
    unitPriceMinor: minor,
    taxable: z.boolean(),
    netMinor: minor,
    taxMinor: minor,
    grossMinor: minor,
    jobId: uuidSchema.nullable(),
    categoryId: uuidSchema.nullable(),
});
export const invoiceDocumentSchema = z.object({
    invoice: invoiceRowSchema.extend({
        termsDays: z.number().int().nullable(),
        /** The rate applied, e.g. "0.15000", or null for a draft never saved. */
        taxRate: z.string().nullable(),
        notes: z.string().nullable(),
        voidReason: z.string().nullable(),
    }),
    lines: z.array(invoiceLineSchema),
    customer: z.object({
        displayName: z.string(),
        legalName: z.string().nullable(),
        email: z.string().nullable(),
        phone: z.string().nullable(),
        addressLine1: z.string().nullable(),
        addressLine2: z.string().nullable(),
        city: z.string().nullable(),
        province: z.string().nullable(),
        postalCode: z.string().nullable(),
        gstHstNumber: z.string().nullable(),
        isTaxExempt: z.boolean(),
    }),
    business: z.object({
        name: z.string(),
        province: z.string(),
        businessNumber: z.string().nullable(),
        address: z.string().nullable(),
        phone: z.string().nullable(),
        email: z.string().nullable(),
        footer: z.string().nullable(),
    }),
});
export const saveInvoiceRequestSchema = z.object({
    customerId: uuidSchema,
    jobId: uuidSchema.nullable().optional(),
    issuedOn: isoDateSchema,
    termsDays: z.number().int().min(0).max(365).optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
    lines: z
        .array(z.object({
        description: z.string().trim().min(1).max(500),
        quantity: z.number().positive().max(1_000_000),
        unitPriceMinor: z.string().regex(/^\d{1,13}$/),
        taxable: z.boolean().default(true),
        jobId: uuidSchema.nullable().optional(),
        categoryId: uuidSchema.nullable().optional(),
    }))
        .min(1)
        .max(100),
});
export const invoiceSettingsSchema = z.object({
    prefix: z.string(),
    nextNumber: z.number().int(),
    nextInvoiceNumber: z.string(),
    businessNumber: z.string().nullable(),
    address: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    footer: z.string().nullable(),
    defaultTermsDays: z.number().int(),
    /** The province's sales tax rate today, e.g. "0.15000000"; null where PST applies. */
    salesTaxRate: z.string().nullable(),
});
export const updateInvoiceSettingsRequestSchema = z
    .object({
    prefix: z.string().trim().max(12),
    nextNumber: z.number().int().min(1),
    businessNumber: z.string().trim().max(40).nullable(),
    address: z.string().trim().max(400).nullable(),
    phone: z.string().trim().max(40).nullable(),
    email: z.string().trim().max(200).nullable(),
    footer: z.string().trim().max(1000).nullable(),
    defaultTermsDays: z.number().int().min(0).max(365),
})
    .partial();
export const createCustomerRequestSchema = z.object({
    displayName: z.string().trim().min(1).max(200),
    kind: z
        .enum(['residential', 'commercial', 'property_manager', 'builder', 'government'])
        .default('residential'),
    primaryEmail: z.email().nullable().optional(),
    primaryPhone: z.string().trim().max(40).nullable().optional(),
    billingAddressLine1: z.string().trim().max(200).nullable().optional(),
    billingCity: z.string().trim().max(100).nullable().optional(),
    billingProvince: z.string().trim().length(2).toUpperCase().nullable().optional(),
    billingPostalCode: z.string().trim().max(10).nullable().optional(),
    isTaxExempt: z.boolean().default(false),
});
//# sourceMappingURL=invoicing.js.map